import { useState, useEffect, useMemo } from 'react';
import {
  Shield, FolderOpen, Tag, FileText, PenSquare,
  LogOut, Plus, Search, X, ChevronRight, Server, Activity, Link as LinkIcon, Mail, Unlink, Wallet, BarChart3, Users, RefreshCw, Gauge
} from 'lucide-react';
import { filesAPI, groupsAPI, devicesAPI } from '../api';
import FilesList from './FilesList';
import FileEditor from './FileEditor';
import CreateFileModal from './CreateFileModal';
import BulkEditor from './BulkEditor';
import RefreshAllExternalSubs from './RefreshAllExternalSubs';
import ExternalSubsOverview from './ExternalSubsOverview';
import GroupBulkEditor from './GroupBulkEditor';
import GroupManager from './GroupManager';
import DealerManager from './DealerManager';
import VpsManager from './VpsManager';
import Subscriptions from './Subscriptions';
import Stats from './Stats';
import AutoTaskLogs from './AutoTaskLogs';
import VanyaVpnLinks from './VanyaVpnLinks';
import GmailAccounts from './GmailAccounts';
import InstallAppButton from './InstallAppButton';
import PushToggle from './PushToggle';
import './Dashboard.css';

function Dashboard({ user, onLogout }) {
  const [files, setFiles] = useState([]);
  const [groups, setGroups] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkEditor, setShowBulkEditor] = useState(false);
  const [showRefreshAllSubs, setShowRefreshAllSubs] = useState(false);
  const [showExtSubsOverview, setShowExtSubsOverview] = useState(false);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [showDealerManager, setShowDealerManager] = useState(false);
  const [showGroupBulkEditor, setShowGroupBulkEditor] = useState(false);
  const [showSubscriptions, setShowSubscriptions] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showVpsManager, setShowVpsManager] = useState(false);
  const [showAutoTaskLogs, setShowAutoTaskLogs] = useState(false);
  const [showVanyaLinks, setShowVanyaLinks] = useState(false);
  const [showGmailAccounts, setShowGmailAccounts] = useState(false);
  const [mobileShowEditor, setMobileShowEditor] = useState(false);
  const [groupBulkTarget, setGroupBulkTarget] = useState(null);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditorDirty, setIsEditorDirty] = useState(false);
  const [cloneInitial, setCloneInitial] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [deviceSummary, setDeviceSummary] = useState({});
  const [unbindingAllFiles, setUnbindingAllFiles] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadDeviceSummary = async () => {
    try {
      const res = await devicesAPI.getAll();
      setDeviceSummary(res.data || {});
    } catch {
      // некритично — значки просто не покажутся
    }
  };

  // Глобальный сброс привязок: ключи везде остаются рабочими, но каждый перепривяжется
  // к текущему устройству при следующем обращении. Задевает сразу всех клиентов, поэтому
  // подтверждение двойное — второй шаг требует напечатать слово, случайно не нажмёшь.
  const handleUnbindAllFiles = async () => {
    const total = Object.keys(deviceSummary).length;
    if (!confirm(
      `Сбросить привязки ВО ВСЕХ файлах (${total})?\n\n` +
      `Ключи НЕ удаляются — ссылки у клиентов останутся рабочими, каждый ключ просто ` +
      `привяжется к новому устройству при следующем подключении.\n\n` +
      `Это затронет всех клиентов сразу.`
    )) return;
    const typed = prompt('Для подтверждения напечатайте: СБРОСИТЬ');
    if ((typed || '').trim().toUpperCase() !== 'СБРОСИТЬ') return;

    setUnbindingAllFiles(true);
    setError('');
    try {
      const res = await devicesAPI.unbindAllFiles();
      const { files = 0, keys = 0 } = res.data || {};
      await loadDeviceSummary();
      alert(`Готово: отвязано ${keys} ключ(ей) в ${files} файле(ах).`);
    } catch (err) {
      setError('Ошибка сброса привязок: ' + (err.response?.data?.error || err.message));
    } finally {
      setUnbindingAllFiles(false);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [filesRes, groupsRes] = await Promise.allSettled([
        filesAPI.getAll(),
        groupsAPI.getAll()
      ]);
      if (filesRes.status === 'fulfilled') setFiles(filesRes.value.data.files);
      else setError('Ошибка загрузки файлов');
      if (groupsRes.status === 'fulfilled') setGroups(groupsRes.value.data.groups || {});
    } finally {
      setIsLoading(false);
    }
    loadDeviceSummary();
  };

  const handleFileSelect = async (fileName) => {
    if (isEditorDirty) {
      if (!confirm('Есть несохранённые изменения. Перейти к другому файлу?')) return;
    }
    setIsEditorDirty(false);
    try {
      const response = await filesAPI.getOne(fileName);
      const fileInfo = files.find(f => f.name === fileName);
      setSelectedFile({ name: fileName, content: response.data.content, group: fileInfo?.group || null });
      setMobileShowEditor(true);
    } catch (err) {
      setError('Ошибка загрузки файла');
    }
  };

  const handleFileClone = async (name) => {
    try {
      const response = await filesAPI.getOne(name);
      const baseName = name.replace(/\.json$/, '');
      setCloneInitial({ content: response.data.content, name: `${baseName}-copy` });
      setShowCreateModal(true);
    } catch (err) {
      setError('Ошибка загрузки файла для копирования');
    }
  };

  const handleFileCreate = async (name, content, groupId) => {
    try {
      await filesAPI.create(name, content, groupId || null);
      await loadData();
      setShowCreateModal(false);
      setCloneInitial(null);
      const fullName = name.endsWith('.json') ? name : `${name}.json`;
      handleFileSelect(fullName);
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Ошибка создания файла');
    }
  };

  const handleFileSave = async (name, content) => {
    try {
      await filesAPI.update(name, content);
      await loadData();
      setIsEditorDirty(false);
      setSelectedFile(prev => ({ ...prev, content }));
      return true;
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Ошибка сохранения файла');
    }
  };

  /**
   * Файл переписан на сервере обновлением внешних подписок — не пользователем в textarea.
   * Кладём новое содержимое туда же, куда его кладёт обычное сохранение, чтобы редактор
   * увидел его как «сохранённое» и не считал изменения несохранёнными.
   */
  const handleExternalRefresh = (name, content) => {
    setSelectedFile(prev => (prev?.name === name ? { ...prev, content } : prev));
    loadData();
  };

  const handleFileDelete = async (name) => {
    if (!confirm(`Удалить файл "${name}"?`)) return;
    try {
      await filesAPI.delete(name);
      await loadData();
      if (selectedFile?.name === name) setSelectedFile(null);
    } catch (err) {
      setError('Ошибка удаления файла');
    }
  };

  const handleCloseEditor = () => { setSelectedFile(null); setIsEditorDirty(false); setMobileShowEditor(false); };

  const handleCloseBulkEditor = async () => {
    setShowBulkEditor(false);
    setSelectedFile(null);
    await loadData();
  };

  const handleCloseGroupBulkEditor = async () => {
    setShowGroupBulkEditor(false);
    setGroupBulkTarget(null);
    await loadData();
  };

  const handleGroupBulkEdit = (groupId) => {
    setGroupBulkTarget(groupId);
    setShowGroupBulkEditor(true);
  };

  const handleGroupChange = async (fileName, groupId) => {
    try {
      await filesAPI.updateGroup(fileName, groupId || null);
      await loadData();
      setSelectedFile(prev => prev ? { ...prev, group: groupId || null } : prev);
    } catch (err) {
      setError('Ошибка смены группы');
    }
  };

  const filteredFiles = files.filter(file =>
    file?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedFiles = useMemo(() => {
    const sorted = [...filteredFiles].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortBy === 'date') cmp = new Date(a.modified) - new Date(b.modified);
      else if (sortBy === 'lines') cmp = a.linesCount - b.linesCount;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    const result = {};
    for (const gId of Object.keys(groups)) result[gId] = [];
    result['__ungrouped'] = [];
    for (const f of sorted) {
      const g = f.group || '__ungrouped';
      if (!result[g]) result[g] = [];
      result[g].push(f);
    }
    return result;
  }, [filteredFiles, groups, sortBy, sortDir]);

  const initials = user.username?.slice(0, 2).toUpperCase() || 'U';

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-logo">
          <div className="header-logo-icon">
            <Shield size={18} />
          </div>
          <span className="header-logo-text">Subscription Manager</span>
        </div>

        <div className="header-actions">
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary btn-sm"
            title="Создать файл"
          >
            <Plus size={14} /> Создать
          </button>

          <div className="header-divider" />

          <button
            onClick={() => setShowSubscriptions(true)}
            className="btn btn-secondary btn-sm"
            title="Подписки: сроки, оплаты, долги"
          >
            <Wallet size={14} /><span className="btn-label"> Подписки</span>
          </button>
          <button
            onClick={() => setShowStats(true)}
            className="btn btn-secondary btn-sm"
            title="Статистика: кто обновлял подписку"
          >
            <BarChart3 size={14} /><span className="btn-label"> Статистика</span>
          </button>
          <button
            onClick={() => setShowVpsManager(true)}
            className="btn btn-secondary btn-sm"
            title="VPS"
          >
            <Server size={14} /><span className="btn-label"> VPS</span>
          </button>
          {/* Временно скрыто по просьбе юзера (2026-08-21). Ничего не удалено:
              экраны AutoTaskLogs и VanyaVpnLinks, их состояние и серверные роуты
              на месте — чтобы вернуть, просто раскомментировать этот блок.

          <button
            onClick={() => setShowAutoTaskLogs(true)}
            className="btn btn-secondary btn-sm"
            title="Auto Task Logs"
          >
            <Activity size={14} /><span className="btn-label"> Auto Task</span>
          </button>
          <button
            onClick={() => setShowVanyaLinks(true)}
            className="btn btn-secondary btn-sm"
            title="Ссылки vpn.com"
          >
            <LinkIcon size={14} /><span className="btn-label"> Получить VPN</span>
          </button>

          */}
          <button
            onClick={() => setShowGmailAccounts(true)}
            className="btn btn-secondary btn-sm"
            title="Gmail-аккаунты для SMS"
          >
            <Mail size={14} /><span className="btn-label"> Gmail</span>
          </button>
          <button
            onClick={() => setShowDealerManager(true)}
            className="btn btn-secondary btn-sm"
            title="Дилеры: логин, пароль и доступные группы"
          >
            <Users size={14} /><span className="btn-label"> Дилеры</span>
          </button>
          <button
            onClick={() => setShowGroupManager(true)}
            className="btn btn-secondary btn-sm"
            title="Группы"
          >
            <FolderOpen size={14} /><span className="btn-label"> Группы</span>
          </button>
          <button
            onClick={() => setShowGroupSelector(true)}
            className="btn btn-secondary btn-sm"
            title="По группе"
          >
            <Tag size={14} /><span className="btn-label"> По группе</span>
          </button>
          <button
            onClick={() => setShowBulkEditor(true)}
            className="btn btn-secondary btn-sm"
            title="Все файлы"
          >
            <PenSquare size={14} /><span className="btn-label"> Все файлы</span>
          </button>
          <button
            onClick={() => setShowRefreshAllSubs(true)}
            className="btn btn-secondary btn-sm"
            title="Обновить все файлы с привязанными внешними подписками"
          >
            <RefreshCw size={14} /><span className="btn-label"> Обновить подписки</span>
          </button>
          <button
            onClick={() => setShowExtSubsOverview(true)}
            className="btn btn-secondary btn-sm"
            title="Остаток трафика и срока по всем внешним подпискам"
          >
            <Gauge size={14} /><span className="btn-label"> Лимиты</span>
          </button>

        </div>

        {/* Polzovatel i vyhod vynes'eny iz skrolliruemogo ryada knopok — inache
            na telefone oni uezzhayut za kray ekrana i do nih ne dobratsya. */}
        <div className="header-user">
          <div className="header-divider" />
          {/* Ustanovka PWA zhivet zdes', a ne v header-actions: tot ryad na telefone
              skrolliruetsya, i knopka uehala by za kray — a nuzhna ona imenno tam. */}
          <InstallAppButton />
          <PushToggle />
          <div className="user-badge">
            <div className="user-avatar">{initials}</div>
            <span className="user-name">{user.username}</span>
          </div>
          <button onClick={onLogout} className="btn btn-ghost btn-sm btn-icon" title="Выйти">
            <LogOut size={15} />
          </button>
        </div>
      </header>

      <div className={`dashboard-body${mobileShowEditor ? ' mobile-editor-active' : ''}`}>
        <div className="files-panel">
          <div className="panel-header">
            <span className="panel-title">Файлы</span>
            <span className="panel-count">{filteredFiles.length}</span>
            <button
              onClick={handleUnbindAllFiles}
              disabled={unbindingAllFiles}
              className="panel-action"
              title="Сбросить привязки во ВСЕХ файлах — ключи останутся рабочими"
            >
              {unbindingAllFiles
                ? <div className="spinner" style={{ width: 12, height: 12, borderWidth: 2, margin: 0 }} />
                : <Unlink size={13} />
              }
            </button>
          </div>

          <div className="search-box">
            <Search size={13} className="search-icon" />
            <input
              type="text"
              placeholder="Поиск..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="search-clear"
                title="Очистить"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {error && <div className="alert alert-error dashboard-alert">{error}</div>}

          <FilesList
            groupedFiles={groupedFiles}
            groups={groups}
            selectedFile={selectedFile?.name}
            onFileSelect={handleFileSelect}
            onFileDelete={handleFileDelete}
            onFileClone={handleFileClone}
            onGroupBulkEdit={handleGroupBulkEdit}
            isLoading={isLoading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
            deviceSummary={deviceSummary}
          />
        </div>

        <button className="mobile-back-btn" onClick={handleCloseEditor}>
          <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Файлы
        </button>

        <div className="editor-panel">
          {selectedFile ? (
            <FileEditor
              file={selectedFile}
              onSave={handleFileSave}
              onClose={handleCloseEditor}
              groups={groups}
              onGroupChange={handleGroupChange}
              onDirtyChange={setIsEditorDirty}
              onKeysChanged={loadDeviceSummary}
              onContentReplaced={handleExternalRefresh}
              /* Замок сразу отражаем в списке — массовые редакторы читают файлы отсюда */
              onLockedChange={(name, locked) =>
                setFiles(prev => prev.map(f => (f.name === name ? { ...f, locked } : f)))
              }
            />
          ) : (
            <div className="editor-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">
                  <FileText size={28} />
                </div>
                <h3>Выберите файл</h3>
                <p>или создайте новый файл подписки</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateFileModal
          onClose={() => { setShowCreateModal(false); setCloneInitial(null); }}
          onCreate={handleFileCreate}
          groups={groups}
          initialContent={cloneInitial?.content || ''}
          initialName={cloneInitial?.name || ''}
        />
      )}

      {showBulkEditor && <BulkEditor onClose={handleCloseBulkEditor} />}

      {showExtSubsOverview && (
        <ExternalSubsOverview onClose={() => setShowExtSubsOverview(false)} />
      )}

      {showRefreshAllSubs && (
        <RefreshAllExternalSubs
          files={files}
          onClose={() => setShowRefreshAllSubs(false)}
          // Содержимое файлов заменилось — перечитываем список, иначе в левой панели
          // останутся старые счётчики строк.
          onRefreshed={loadData}
        />
      )}

      {showGroupBulkEditor && groupBulkTarget && (
        <GroupBulkEditor
          groupId={groupBulkTarget}
          groupName={groups[groupBulkTarget]?.name || groupBulkTarget}
          files={files}
          onClose={handleCloseGroupBulkEditor}
        />
      )}

      {showVpsManager && (
        <VpsManager
          groups={groups}
          onClose={() => setShowVpsManager(false)}
        />
      )}

      {showDealerManager && (
        <DealerManager
          groups={groups}
          onClose={() => setShowDealerManager(false)}
        />
      )}

      {showGroupManager && (
        <GroupManager
          groups={groups}
          onGroupsChange={() => loadData()}
          onClose={() => setShowGroupManager(false)}
        />
      )}

      {showSubscriptions && (
        <div className="fullscreen-overlay">
          <Subscriptions onClose={() => { setShowSubscriptions(false); loadData(); }} />
        </div>
      )}

      {showStats && (
        <div className="fullscreen-overlay">
          <Stats onClose={() => setShowStats(false)} />
        </div>
      )}

      {showAutoTaskLogs && (
        <AutoTaskLogs onClose={() => setShowAutoTaskLogs(false)} />
      )}

      {showVanyaLinks && (
        <VanyaVpnLinks onClose={() => setShowVanyaLinks(false)} />
      )}

      {showGmailAccounts && (
        <GmailAccounts onClose={() => setShowGmailAccounts(false)} />
      )}

      {showGroupSelector && (
        <div className="modal-overlay" onClick={() => setShowGroupSelector(false)}>
          <div className="group-selector-modal" onClick={e => e.stopPropagation()}>
            <div className="group-selector-header">
              <h3><Tag size={16} /> Выберите группу</h3>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setShowGroupSelector(false)}>
                <X size={15} />
              </button>
            </div>
            <div className="group-selector-list">
              {Object.entries(groups).length === 0 ? (
                <p className="group-selector-empty">
                  Нет созданных групп. Создайте группу через кнопку «Группы».
                </p>
              ) : (
                Object.entries(groups).map(([id, g]) => {
                  const count = files.filter(f => f.group === id).length;
                  return (
                    <button
                      key={id}
                      className="group-selector-item"
                      onClick={() => {
                        setGroupBulkTarget(id);
                        setShowGroupBulkEditor(true);
                        setShowGroupSelector(false);
                      }}
                    >
                      <span className="group-selector-name">
                        <Tag size={14} /> {g.name}
                      </span>
                      <span className="group-selector-count">{count} файлов</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
